/* ──────────────────────────────────────────────────────────────────────────
 * Proving the solution — §22.
 *
 * The brief opens the section with the instruction that shapes everything
 * here:
 *
 *   "Do not try to prove superiority through screenshots. Run controlled
 *    scenarios and measure."
 *
 * It then lists thirteen metrics. This computes every one of them from the
 * §21 mission runs, and returns null with a reason for those that cannot be
 * measured by a browser with no sensors, no dispatch integration and no
 * ground truth. Four are unmeasurable in principle here — detection
 * latency, false-alert rate, field response and resolution time. The rest
 * depend on what the caller supplies, so the counts are computed and
 * reported rather than asserted in this comment.
 *
 * ── WHY THE NULLS ARE THE MOST IMPORTANT PART ──
 * A metrics page showing thirteen green numbers would be the single most
 * misleading artefact this platform could produce, and it would be the
 * easiest to build. Detection latency requires something that detects.
 * Officer acknowledgement time requires an officer. False-positive rate
 * requires a labelled incident set from an event that has not happened.
 *
 * Each unmeasurable metric therefore carries `whatWouldBeNeeded` — the
 * specific integration or exercise that would make it real. That turns the
 * gap list into a pilot design, which is what §21 asked for anyway.
 *
 * Every metric also carries `comparable: false` where the figure measures
 * the platform against itself rather than against a baseline. The brief's
 * closing line is "then government can compare operational performance
 * with its baseline" — and a number with no baseline cannot do that,
 * however precisely it is computed.
 * ────────────────────────────────────────────────────────────────────────── */

import { runAllMissions } from "./missions";
import { buildTwin } from "../twin/twin";
import { provenanceSummary } from "../spine/evidenceEnvelope";

const measured = (value, unit, note, { comparable = false } = {}) => ({
  measurable: true, value, unit, note, comparable,
});

const unmeasurable = (reason, whatWouldBeNeeded) => ({
  measurable: false, value: null, reason, whatWouldBeNeeded, comparable: false,
});

/* The thirteen, in the brief's order. */
export const METRIC_IDS = [
  "detectionLatency", "predictionLeadTime", "falsePositiveRate",
  "incidentCreationTime", "decisionSupportTime", "officerAcknowledgementTime",
  "crossAgencyAssignmentTime", "fieldResponseTime", "resolutionTime",
  "predictionAccuracy", "interventionEffectiveness", "systemAvailability",
  "evidenceCompleteness",
];

export function computeProofMetrics({
  liveState,
  twin = null,
  missionRuns = null,
  accuracy = null,
  envelopes = [],
  sessionStartedAt = null,
  simulateOperator = false,
} = {}) {
  const t = twin ?? (liveState ? buildTwin(liveState) : null);
  const runs = missionRuns
    ?? (liveState ? runAllMissions(liveState, { twin: t, simulateOperator }) : []);

  const byId = Object.fromEntries(runs.map((r) => [r.id, r]));
  const stageMs = (runId, stageName) =>
    byId[runId]?.stages?.find((s) => s.stage === stageName)?.ms ?? null;

  /* 1. Detection latency — time from the event occurring to the platform
   *    noticing. Requires something that detects. */
  const detectionLatency = unmeasurable(
    "Nothing in this platform detects anything. Crowd state is generated by a model rather than observed, so there is no gap between an event occurring and the platform knowing about it — the platform IS the event.",
    "A video analytics or sensor feed, plus an independently timestamped record of when the condition actually began.",
  );

  /* 2. Prediction lead time — GENUINELY measurable. This is the platform's
   *    strongest honest claim: how far ahead a band change is called. */
  const leads = runs.map((r) => r.leadMinutes).filter((x) => x != null);
  const predictionLeadTime = leads.length
    ? measured(
        Math.round(leads.reduce((a, b) => a + b, 0) / leads.length),
        "minutes",
        "Minutes between the forecast being made and the projected level-of-service band change. Measured within the model, so it is a property of the forecast horizon, not evidence the prediction is correct.",
      )
    : unmeasurable(
        "No mission produced a projected band change within the 60-minute horizon during this run.",
        "A run started closer to a ritual peak, where a band change occurs inside the horizon.",
      );

  /* 3. False positive / negative — the honest null. */
  const falsePositiveRate = unmeasurable(
    "There is no labelled ground-truth incident set. Simhastha 2027 has not happened, and no historical Kumbh incident log is loaded. Any precision, recall or F1 figure quoted here would be fabricated.",
    "A validation set of historical incidents with confirmed outcomes, or a controlled field exercise with independently recorded ground truth.",
  );

  /* 4. Incident creation time — measurable: signal to incident record. */
  const creationMs = [
    stageMs("crowd_surge", "sense_crowd"),
    stageMs("crowd_surge", "fuse_context"),
    stageMs("crowd_surge", "understand"),
  ].filter((x) => x != null);
  const incidentCreationTime = creationMs.length
    ? measured(
        Number(creationMs.reduce((a, b) => a + b, 0).toFixed(1)),
        "ms",
        "Compute time from signal to a structured, classified incident with a located sector. Machine time only — it excludes any human step.",
      )
    : unmeasurable("No mission recorded the creation stages.", "A completed crowd-surge mission run.");

  /* 5. Decision-support time — measurable, and the number that most
   *    directly justifies the platform: how long to produce ranked,
   *    costed options from a live signal. */
  const decisionMs = [
    stageMs("crowd_surge", "predict"),
    stageMs("crowd_surge", "simulate"),
    stageMs("crowd_surge", "decide"),
  ].filter((x) => x != null);
  const decisionSupportTime = decisionMs.length
    ? measured(
        Number(decisionMs.reduce((a, b) => a + b, 0).toFixed(1)),
        "ms",
        "Compute time from signal to eight ranked intervention options with projected consequences and displacement. This is the figure that would be compared against how long the same analysis takes a control room today.",
        { comparable: true },
      )
    : unmeasurable("No mission recorded the decision stages.", "A completed crowd-surge mission run.");

  /* 6. Officer acknowledgement — needs an officer. */
  const officerAcknowledgementTime = simulateOperator
    ? unmeasurable(
        "A simulated officer acknowledged instantly. That measures the runner, not operations, and is deliberately not reported as a duration.",
        "A field exercise with real officers acknowledging on their own devices.",
      )
    : unmeasurable(
        "No human is in the loop. Every mission halts at AUTHORIZE rather than simulating an acknowledgement.",
        "A field exercise with real officers acknowledging on their own devices.",
      );

  /* 7. Cross-agency assignment time — measurable as compute; the human
   *    half is not. */
  const assignMs = stageMs("multi_hazard", "assign_agencies");
  const crossAgencyAssignmentTime = assignMs != null
    ? measured(
        assignMs, "ms",
        "Compute time to derive which departments a multi-hazard condition implicates, from the hazards actually elevated. The time for those departments to accept the assignment is a human process and is not included.",
      )
    : unmeasurable("No multi-hazard mission run.", "A completed multi-hazard mission run.");

  /* 8. Field response time — needs the field. */
  const fieldResponseTime = unmeasurable(
    "No unit has ever been dispatched by this platform. Response ETAs are computed from invented unit positions and a stated crowd-speed curve; they are estimates, not observations.",
    "Integration with 108/police dispatch and GPS telemetry from responding units.",
  );

  /* 9. Resolution time — needs a real incident lifecycle. */
  const resolutionTime = unmeasurable(
    "No real incident has been resolved. Mission runs with simulateOperator produce synthetic timestamps that measure nothing.",
    "Live operation, or a field exercise run end to end with real timings.",
  );

  /* 10. Prediction accuracy — measurable, with the caveat that matters. */
  const predictionAccuracy = accuracy?.available
    ? measured(
        accuracy.byHorizon,
        "MAPE % by horizon",
        "Scored against what the model itself later produced, not against observed reality. It demonstrates internal consistency and reproducibility. It is NOT evidence the platform predicts real crowds well, and must never be quoted as such.",
      )
    : unmeasurable(
        accuracy?.reason ?? "No forecast has reached its target time yet.",
        "A longer run, so recorded forecasts reach their target minute — or real observations to score against.",
      );

  /* 11. Intervention effectiveness — measurable against the model's own
   *     counterfactual, which is a genuine comparison but not a real one. */
  const surge = byId.crowd_surge;
  const interventionEffectiveness = surge?.outcome?.verified
    ? measured(
        {
          densityDoNothing: surge.outcome.densityDoNothing,
          densityWithOption: surge.outcome.densityWithOption,
          improvement: surge.outcome.improvement,
        },
        "persons/m² avoided",
        "Measured against the model's own do-nothing run over the same 30 minutes. A genuine counterfactual comparison within the simulation — and no evidence at all about the real world.",
      )
    : unmeasurable(
        surge?.awaitingAuthorisation
          ? "The crowd-surge mission halted at AUTHORIZE, so no intervention was applied."
          : "No verified intervention outcome in this run.",
        "A mission run with an approved intervention, or a real intervention with before/after measurement.",
      );

  /* 12. System availability. */
  const systemAvailability = sessionStartedAt
    ? measured(
        Number(((Date.now() - sessionStartedAt) / 60000).toFixed(1)),
        "minutes of session uptime",
        "Browser session uptime. This is not a service-availability figure — there is no service, no SLA and no monitoring.",
      )
    : unmeasurable(
        "No session start time supplied.",
        "A deployed service with uptime monitoring.",
      );

  /* 13. Evidence completeness — genuinely measurable, and one of the
   *     strongest numbers here because it measures a discipline rather
   *     than a capability. */
  const envSummary = envelopes.length ? provenanceSummary(envelopes) : null;
  const missionEnvelope = byId.multi_hazard?.evidenceCompleteness ?? null;
  const evidenceCompleteness = envSummary?.measurable
    ? measured(
        envSummary.averageCompleteness, "% of 16 fields",
        `Average across ${envSummary.total} decision records. Weakest fields: ${envSummary.weakestFields.map((w) => w.field).join(", ")}.`,
        { comparable: true },
      )
    : missionEnvelope != null
      ? measured(
          missionEnvelope, "% of 16 fields",
          "From the multi-hazard mission's evidence envelope. Counts fields actually filled, not fields defined.",
          { comparable: true },
        )
      : unmeasurable("No evidence envelopes were produced.", "A completed mission run or a closed incident.");

  const metrics = {
    detectionLatency, predictionLeadTime, falsePositiveRate,
    incidentCreationTime, decisionSupportTime, officerAcknowledgementTime,
    crossAgencyAssignmentTime, fieldResponseTime, resolutionTime,
    predictionAccuracy, interventionEffectiveness, systemAvailability,
    evidenceCompleteness,
  };

  const measurableCount = METRIC_IDS.filter((k) => metrics[k].measurable).length;
  const comparableCount = METRIC_IDS.filter((k) => metrics[k].comparable).length;

  return {
    metrics,
    missionRuns: runs,
    total: METRIC_IDS.length,
    measurable: measurableCount,
    unmeasurable: METRIC_IDS.length - measurableCount,
    comparable: comparableCount,
    /* The paragraph a reviewer should read before the numbers. */
    statement:
      `${measurableCount} of ${METRIC_IDS.length} metrics can be measured by this platform. `
      + `${METRIC_IDS.length - measurableCount} cannot, because they require sensors, dispatch integration, human operators or a labelled ground-truth set that does not exist. `
      + `Of the measurable ones, ${comparableCount} could be compared against a government baseline today; the rest measure the platform against itself.`,
    /* The gap list doubles as a pilot design — which is what §21 asked
     * for in the first place. */
    pilotRequirements: METRIC_IDS
      .filter((k) => !metrics[k].measurable)
      .map((k) => ({ metric: k, needs: metrics[k].whatWouldBeNeeded })),
  };
}

export const PROOF_ASSUMPTIONS = [
  "The metrics that can be computed are computed from real mission runs; the rest return null with the specific integration or exercise that would make them measurable. How many fall in each group depends on what is supplied — prediction accuracy needs a scored forecast, and intervention effectiveness needs an approved intervention — so the counts are computed rather than stated.",
  "Compute timings are genuinely measured with performance.now(). They are machine time and exclude every human step, which is stated on each one.",
  "Prediction accuracy scores the model against its own later output. It shows internal consistency and reproducibility, never real-world accuracy, and must not be quoted as the latter.",
  "Intervention effectiveness is a counterfactual within the simulation. It is a genuine comparison and no evidence about the real world.",
  "Metrics carry a `comparable` flag. A number with no government baseline cannot support the brief's closing goal of comparing operational performance, however precisely it is computed.",
  "A simulated officer acknowledgement is not reported as a duration. Reporting it would measure the test runner and present it as an operational figure.",
];
